<template>
  <v-navigation-drawer
    v-model="drawer"
    :expand-on-hover="expandOnHover"
    :right="$vuetify.rtl"
    mobile-break-point="960"
    app
    width="280"
    v-bind="$attrs"
    class="modern-drawer"
    id="user-navigation-drawer"
  >
    <!-- Header Section -->
    <div class="drawer-header">
      <div class="header-background"></div>
      <div class="header-content">
        <div class="avatar-container">
          <div
            class="avatar-ring"
            :class="{ online: user.status === 'online' }"
          >
            <v-img
              v-if="user.avatar"
              :src="user.avatar"
              :alt="user.user_name"
              class="user-avatar"
              width="64"
              height="64"
            />
            <v-img
              v-else
              src="ovec.png"
              :alt="user.user_name"
              class="user-avatar"
              width="64"
              height="64"
            />
          </div>
          <div
            class="status-dot"
            :class="user.status === 'online' ? 'online' : 'offline'"
          ></div>
        </div>

        <div class="user-info">
          <p class="user-name">{{ user.user_name || "ผู้ใช้งาน" }}</p>
          <p class="user-role">{{ user.user_status }}</p>
          <p class="user-fullname">
            {{ user.user_firstname }} {{ user.user_lastname }}
          </p>
        </div>
      </div>
    </div>

    <v-divider class="header-divider" />

    <!-- Search Section -->
    <div class="search-section">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        placeholder="ค้นหาเมนู..."
        hide-details
        dense
        solo
        flat
        clearable
        class="search-field"
        background-color="transparent"
      />
    </div>

    <!-- Navigation Menu -->
    <div class="navigation-content">
      <v-list class="nav-list" nav>        
        <template v-for="(item, i) in filteredMenuItems">
          <!-- Menu Group (has children) -->
          <div
            v-if="item.items && item.items.length > 0"
            :key="'group-' + i"
            class="nav-group"
          >
            <v-list-group
              :value="item.active"
              :prepend-icon="item.action"
              no-action
              class="nav-group-item"
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title class="group-title">
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-chip
                  v-if="item.badge"
                  x-small
                  :color="item.badgeColor || 'primary'"
                  class="group-badge"
                >
                  {{ item.badge }}
                </v-chip>
              </template>

              <v-list-item
                v-for="child in item.items"
                :key="child.title"
                :to="child.to"
                class="nav-child-item"
                exact
              >
                <v-list-item-icon class="child-icon">
                  <v-icon size="20">{{ child.action }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="child-title">
                    {{ child.title }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-chip
                  v-if="child.badge"
                  x-small
                  :color="child.badgeColor || 'info'"
                  class="child-badge"
                >
                  {{ child.badge }}
                </v-chip>
              </v-list-item>
            </v-list-group>
          </div>

          <!-- Single Menu Item (no children) -->
          <v-list-item
            v-else
            :key="'item-' + i"
            :to="item.to"
            class="nav-single-item"
            exact
          >
            <v-list-item-icon>
              <v-icon>{{ item.action }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
            <v-chip
              v-if="item.badge"
              x-small
              :color="item.badgeColor || 'primary'"
            >
              {{ item.badge }}
            </v-chip>
          </v-list-item>
        </template>

        <!-- No Results -->
        <div
          v-if="filteredMenuItems.length === 0 && searchQuery"
          class="no-results"
        >
          <v-icon size="40" color="grey lighten-1">mdi-magnify-close</v-icon>
          <p class="no-results-text">ไม่พบเมนูที่ค้นหา</p>
        </div>
      </v-list>
    </div>

    <!-- Footer Section -->
    <template v-slot:append>
      <div class="drawer-footer">
        <v-divider class="footer-divider" />

        <!-- Quick Actions -->
        <div class="footer-actions">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                @click="goToSettings"
                v-bind="attrs"
                v-on="on"
                class="action-btn"
              >
                <v-icon size="20">mdi-cog-outline</v-icon>
              </v-btn>
            </template>
            <span>ตั้งค่า</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                @click="toggleTheme"
                v-bind="attrs"
                v-on="on"
                class="action-btn"
              >
                <v-icon size="20">
                  {{
                    isDark
                      ? "mdi-white-balance-sunny"
                      : "mdi-moon-waning-crescent"
                  }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ isDark ? "โหมดสว่าง" : "โหมดมืด" }}</span>
          </v-tooltip>

          <v-spacer />

          <v-chip x-small outlined class="version-chip"
            >v{{ appVersion }}</v-chip
          >
        </div>

        <!-- Logout Button -->
        <v-btn block depressed class="logout-btn" @click="logout">
          <v-icon left size="18">mdi-logout</v-icon>
          ออกจากระบบ
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "DashboardUserDrawer",

  props: {
    expandOnHover: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      user: {},
      searchQuery: "",
      isDark: false,
      appVersion: "2.1.0",

      menuItems: [
        {
          title: "แดชบอร์ด & ระบบ",
          action: "mdi-view-dashboard",
          authority: "userauthorityAdmin",
          items: [
            {
              title: "แผงควบคุม",
              action: "mdi-chart-multiple",
              to: "/admin/Dashboard",
              badge: "หลัก"
            },
            {
              title: "ตั้งค่าระบบ",
              action: "mdi-cog",
              to: "/admin/setting"
            }
          ]
        },
        {
          title: "ผู้ใช้งานระบบ",
          action: "mdi-account-group",
          authority: "all",
          items: [
            {
              title: "ครูและบุคลากร",
              action: "mdi-account-school",
              to: "/admin/personnel"
            },
            {
              title: "วิทยาลัย สอจ.สถาบัน กจ2",
              action: "mdi-school",
              to: "/admin/user"
            },
            {
              title: "สถานะผู้ใช้งานระบบ",
              action: "mdi-account-cog",
              to: "/admin/user_status"
            }
          ]
        },
        {
          title: "ข้อมูลสถานศึกษา",
          action: "mdi-school",
          authority: "all",
          items: [
            {
              title: "ประเภทสถานศึกษา",
              action: "mdi-office-building",
              to: "/admin/collegetype"
            },
            {
              title: "ข้อมูลสถานศึกษา",
              action: "mdi-domain",
              to: "/admin/college"
            },
            {
              title: "รายละเอียดสถานศึกษา",
              action: "mdi-information",
              to: "/admin/collegeinfo"
            }
          ]
        },
        {
          title: "ระบบงานบุคคล",
          action: "mdi-account-tie",
          authority: "all",
          items: [
            {
              title: "ข้อมูลบุคคล",
              action: "mdi-account",
              to: "/admin/personnel_tem"
            },
            {
              title: "ขอย้ายบุคคล",
              action: "mdi-account-switch",
              to: "/admin/collegepersonnelreq"
            },
            {
              title: "คุณวุฒิการศึกษา",
              action: "mdi-school",
              to: "/admin/personnel_education"
            },
            {
              title: "ประสบการณ์",
              action: "mdi-briefcase",
              to: "/admin/personnel_experience"
            },
            {
              title: "ประวัติการรับราชการ",
              action: "mdi-history",
              to: "/admin/personnel_work_history"
            },
            {
              title: "ผลงาน รางวัล",
              action: "mdi-trophy",
              to: "/admin/personnel_award"
            },
            {
              title: "โทษ วินัย",
              action: "mdi-gavel",
              to: "/admin/personnel_discipline"
            }
          ]
        },
        {
          title: "ระบบการย้าย (ครู)",
          action: "mdi-account-switch",
          authority: "userauthorityMoveTeach",
          badgeColor: "success",
          items: [
            {
              title: "สายการสอนและสนับสนุน",
              action: "mdi-account-arrow-right",
              to: "/admin/transference_personnel"
            },
            {
              title: "สายงานบริหารสถานศึกษา",
              action: "mdi-account-supervisor",
              to: "/admin/transference_manage"
            },
            {
              title: "การย้ายกรณีพิเศษ",
              action: "mdi-account-star",
              to: "/admin/transference_special"
            }
          ]
        },
        {
          title: "ระบบการย้าย (บริหาร)",
          action: "mdi-account-supervisor-circle",
          authority: "userauthorityMoveManage",
          items: [
            {
              title: "สายการสอนและสนับสนุน",
              action: "mdi-account-arrow-right",
              to: "/admin/transference_personnel_manage"
            },
            {
              title: "สายงานบริหารสถานศึกษา",
              action: "mdi-account-supervisor",
              to: "/admin/transference_manage_admin"
            },
            {
              title: "การย้ายกรณีพิเศษ",
              action: "mdi-account-star",
              to: "/admin/transference_special_manage"
            }
          ]
        },
        {
          title: "ระบบเงื่อนไขสาขาวิชา",
          action: "mdi-school-outline",
          authority: "userauthorityMoveTeach",
          items: [
            {
              title: "สาขาวิชาเอก",
              action: "mdi-book-open",
              to: "/admin/branch"
            },
            {
              title: "เงื่อนไขสาขาวิชาเอก",
              action: "mdi-file-document",
              to: "/admin/conditions_transfer"
            },
            {
              title: "สถิติเงื่อนไขสาขาวิชา",
              action: "mdi-chart-box",
              to: "/admin/conditions_statistics"
            }
          ]
        },
        {
          title: "ระบบอัตรากำลัง",
          action: "mdi-account-multiple",
          authority: "userauthorityManpower",
          items: [
            {
              title: "อัตรากำลังสถานศึกษา",
              action: "mdi-account-group",
              to: "/admin/man_power"
            },
            {
              title: "วิเคราะห์อัตรากำลัง",
              action: "mdi-chart-line",
              to: "/admin/man_power_analysis"
            }
          ]
        },
        {
          title: "ระบบครูผู้ช่วย",
          action: "mdi-account-school",
          authority: "userauthorityAssitantTeach",
          items: [
            {
              title: "รายงานตัวครูผู้ช่วย",
              action: "mdi-account-plus",
              to: "/admin/assistant_teacher_report/manage_assistant_teacher"
            },
            {
              title: "ตำแหน่งรับรายงานตัว",
              action: "mdi-map-marker",
              to: "/admin/assistant_teacher_report/manage_college_manpower"
            },
            {
              title: "การเลือกสถานศึกษา",
              action: "mdi-school-outline",
              to:
                "/admin/assistant_teacher_report/teacher_college_manpower_report"
            },
            {
              title: "การรายงานตัว ณ สถานศึกษา",
              action: "mdi-clipboard-check",
              to:
                "/admin/assistant_teacher_report/manage_assistant_CollegeReport"
            },
            {
              title: "การประเมินครูผู้ช่วย",
              action: "mdi-star",
              to: "/admin/assistant_teacher"
            }
          ]
        },

        {
          title: "บริหารกรอบอัตรากำลัง",
          action: "mdi-account-tie",
          authority: "userauthority_govem",
          items: [
            {
              title: "กำหนดการรายงานข้อมูลกรอบอัตรากำลัง",
              action: "mdi-numeric-1-box",
              to: "/admin/gov_em/period_mgovem"
            },
            {
              title: "การรายงานกรอบอัตรากำลังของสถานศึกษา",
              action: "mdi-numeric-2-box",
              to: "/admin/gov_em/Governmentemployeereportadmin"
            },

            {
              title: "กรอบอัตรากำลัง พนักงานราชการ ส่วนกลาง",
              action: "mdi-numeric-3-box",
              to: "/admin/gov_em_vec/government_employee"
            },
            {
              title: "ข้อมูล กรอบอัตรากำลัง",
              action: "mdi-numeric-4-box",
              to: "/admin/gov_em/government_employee"
            },
            {
              title: "รายงาน กรอบอัตรากำลัง พนักงานราชการ",
              action: "mdi-numeric-5-box",
              to: "/admin/gov_em/government_employee_report"
            },

            {
              title: "รายงาน ข้อมูลการเบิกจ่ายงบประมาณ สนผ.",
              action: "mdi-numeric-6-box",
              to: "/admin/government_employee_report_snp"
            }
          ]
        },
        {
          title: "การประเมินผลการปฏิบัติงาน",
          action: "mdi-account-tie",
          authority: "userauthority_govem",
          items: [
            {
              title: "ประวัติการประเมินผลการปฏิบัติงาน",
              action: "mdi-numeric-7-box",
              to: "/admin/gov_em/admin_assessment_report"
            },
            {
              title: "ประวัติการลาออก",
              action: "mdi-numeric-8-box",
              to: "/admin/gov_em/ResignHistoryPage"
            },
            {
              title: "คำตอบแทน ปี พ.ศ. 2568",
              action: "mdi-numeric-9-box",
              to: "/admin/gov_em/salary_gov_2568"
            }
          ]
        },

        {
          title: "ข่าวสาร & คู่มือ",
          action: "mdi-newspaper",
          authority: "all",
          items: [
            {
              title: "ข่าวสาร",
              action: "mdi-newspaper",
              to: "/admin/news_s",
              badgeColor: "error"
            },
            {
              title: "คู่มือใช้งาน",
              action: "mdi-book",
              to: "/admin/manual_s"
            }
          ]
        }
      ]
    };
  },

  computed: {
    ...mapState(["barColor", "barImage"]),

    drawer: {
      get() {
        return this.$store.state.drawer;
      },
      set(val) {
        this.$store.commit("SET_DRAWER", val);
      }
    },

    filteredMenuItems() {
      const userAuthorities = this.getUserAuthorities();
      let items = this.menuItems.filter(
        item =>
          item.authority === "all" || userAuthorities.includes(item.authority)
      );

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        items = items
          .filter(item => {
            if (item.title.toLowerCase().includes(query)) return true;
            if (item.items) {
              return item.items.some(sub =>
                sub.title.toLowerCase().includes(query)
              );
            }
            return false;
          })
          .map(item => ({
            ...item,
            items: item.items
              ? item.items.filter(
                  sub =>
                    sub.title.toLowerCase().includes(query) ||
                    item.title.toLowerCase().includes(query)
                )
              : []
          }));
      }

      return items;
    }
  },

  async mounted() {
    document.documentElement.classList.toggle("no-grayscale");
    await this.loadUserData();
    this.isDark = this.$vuetify.theme.dark;
  },

  methods: {
    async loadUserData() {
      try {
        const userSession = JSON.parse(sessionStorage.getItem("user")) || {};
        if (userSession.user_name) {
          const result = await this.$http.post("admin.php", {
            user_name: userSession.user_name,
            ApiKey: "HRvec2021"
          });
          this.user = {
            ...result.data,
            status: "online"
          };

          if (this.user.user_update === "0") {
            this.user.ApiKey = "HRvec2021";
            this.user.user_update = 1;
            await this.$http.post("user.update.php", this.user);
            window.location.reload();
          }
        }else{
          console.warn("No user session found. Redirecting to login.");
            sessionStorage.clear();
          localStorage.removeItem("authToken");
          this.$router.push("/");
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    },

    getUserAuthorities() {
      const authorities = [];
      if (this.user.userauthorityAdmin === "1")
        authorities.push("userauthorityAdmin");
      if (this.user.userauthorityAssitantTeach === "1")
        authorities.push("userauthorityAssitantTeach");
      if (this.user.userauthorityManpower === "1")
        authorities.push("userauthorityManpower");
      if (this.user.userauthorityMoveManage === "1")
        authorities.push("userauthorityMoveManage");
      if (this.user.userauthorityMoveTeach === "1")
        authorities.push("userauthorityMoveTeach");
      if (this.user.userauthorityHR === "1")
        authorities.push("userauthorityHR");
      if (this.user.userauthority_govem === "1")
        authorities.push("userauthority_govem");
      return authorities;
    },

    toggleTheme() {
      this.isDark = !this.isDark;
      this.$vuetify.theme.dark = this.isDark;
      localStorage.setItem("darkTheme", this.isDark);
    },

    goToSettings() {
      this.$router.push("/admin/setting");
    },

    logout() {
      Swal.fire({
        title: "ยืนยันการออกจากระบบ",
        text: "คุณต้องการออกจากระบบหรือไม่?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#e53e3e",
        cancelButtonColor: "#a0aec0",
        confirmButtonText: "ออกจากระบบ",
        cancelButtonText: "ยกเลิก"
      }).then(result => {
        if (result.isConfirmed) {
          sessionStorage.clear();
          localStorage.removeItem("authToken");
          this.$router.push("/");
        }
      });
    }
  }
};
</script>

<style scoped>
/* ===== BASE DRAWER ===== */
.modern-drawer {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
  border-right: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.06) !important;
}

/* ===== HEADER ===== */
.drawer-header {
  position: relative;
  padding: 1.75rem 1.5rem 1.25rem;
  overflow: hidden;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #1a365d;
  opacity: 1;
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.avatar-container {
  position: relative;
  margin-bottom: 0.75rem;
}

.avatar-ring {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  padding: 3px;
  background: rgba(255, 255, 255, 0.25);
  transition: all 0.3s ease;
}

.avatar-ring.online {
  background: rgba(72, 187, 120, 0.7);
}

.avatar-ring:hover {
  transform: scale(1.06);
  background: rgba(255, 255, 255, 0.4);
}

.user-avatar {
  border-radius: 50%;
  width: 62px;
  height: 62px;
  object-fit: cover;
}

.status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2.5px solid #1a365d;
}

.status-dot.online {
  background: #48bb78;
  box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.5);
  animation: pulse-status 2.5s infinite;
}

.status-dot.offline {
  background: #a0aec0;
}

@keyframes pulse-status {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(72, 187, 120, 0);
  }
}

.user-info {
  color: #ffffff;
}

.user-name {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 0.15rem;
  letter-spacing: 0.01em;
}

.user-role {
  font-size: 0.78rem;
  font-weight: 500;
  opacity: 0.85;
  margin: 0 0 0.1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.user-fullname {
  font-size: 0.8rem;
  opacity: 0.75;
  margin: 0;
  font-weight: 400;
}

.header-divider {
  border-color: rgba(0, 0, 0, 0.06) !important;
  margin: 0;
}

/* ===== SEARCH ===== */
.search-section {
  padding: 0.75rem 1rem;
  background: rgba(247, 250, 252, 0.8);
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.search-field {
  border-radius: 10px !important;
  font-size: 0.85rem;
}

/* ===== NAVIGATION ===== */
.navigation-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

.nav-list {
  padding: 0 !important;
}

.nav-group {
  margin: 0.15rem 0.75rem;
}

.nav-group-item {
  border-radius: 10px !important;
  overflow: hidden;
}

.group-title {
  font-weight: 600;
  font-size: 0.88rem;
  color: #2d3748;
}

.group-badge {
  font-size: 0.65rem !important;
  height: 18px !important;
  font-weight: 700;
}

.nav-child-item {
  border-radius: 8px !important;
  margin: 0.1rem 0;
  min-height: 38px;
  transition: all 0.25s ease !important;
}

.nav-child-item:hover {
  background: rgba(49, 130, 206, 0.08) !important;
  transform: translateX(3px);
}

.nav-child-item.v-list-item--active {
  background: rgba(49, 130, 206, 0.1) !important;
  border-left: 3px solid #3182ce;
  color: #2b6cb0 !important;
}

.nav-child-item.v-list-item--active .v-icon {
  color: #3182ce !important;
}

.child-icon {
  margin-right: 0 !important;
  min-width: 30px !important;
}

.child-title {
  font-size: 0.84rem;
  font-weight: 450;
}

.child-badge {
  font-size: 0.6rem !important;
  height: 16px !important;
}

.nav-single-item {
  margin: 0.15rem 0.75rem;
  border-radius: 10px !important;
  transition: all 0.25s ease !important;
}

.nav-single-item:hover {
  background: rgba(49, 130, 206, 0.08) !important;
  transform: translateX(3px);
}

.nav-single-item.v-list-item--active {
  background: rgba(49, 130, 206, 0.1) !important;
  border-left: 3px solid #3182ce;
  color: #2b6cb0 !important;
}

/* No Results */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  opacity: 0.6;
}

.no-results-text {
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: #a0aec0;
}

/* ===== FOOTER ===== */
.drawer-footer {
  padding: 0.5rem 0.75rem 0.75rem;
  background: rgba(247, 250, 252, 0.6);
}

.footer-divider {
  border-color: rgba(0, 0, 0, 0.06) !important;
  margin-bottom: 0.5rem;
}

.footer-actions {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.25rem 0.5rem;
  gap: 0.25rem;
}

.action-btn {
  color: #718096 !important;
  transition: all 0.25s ease;
}

.action-btn:hover {
  color: #3182ce !important;
  background: rgba(49, 130, 206, 0.08) !important;
}

.version-chip {
  font-size: 0.65rem !important;
  height: 20px !important;
  opacity: 0.5;
}

.logout-btn {
  background: #e53e3e !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: none;
  letter-spacing: 0.02em;
  height: 40px !important;
  transition: background 0.2s ease !important;
}

.logout-btn:hover {
  background: #c53030 !important;
}

/* ===== SCROLLBAR ===== */
.navigation-content::-webkit-scrollbar {
  width: 4px;
}

.navigation-content::-webkit-scrollbar-track {
  background: transparent;
}

.navigation-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 2px;
}

.navigation-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 960px) {
  .drawer-header {
    padding: 1.25rem 1rem 1rem;
  }

  .avatar-ring {
    width: 56px;
    height: 56px;
  }

  .user-avatar {
    width: 50px;
    height: 50px;
  }

  .user-name {
    font-size: 0.95rem;
  }

  .nav-group,
  .nav-single-item {
    margin: 0.1rem 0.5rem;
  }
}

/* ===== ACCESSIBILITY ===== */
.nav-child-item:focus-visible,
.nav-single-item:focus-visible {
  outline: 2px solid #3182ce;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}

/* ===== PRINT ===== */
@media print {
  .modern-drawer {
    display: none !important;
  }
}
</style>

<style lang="sass">
@import '~vuetify/src/styles/tools/_rtl.sass'

#user-navigation-drawer
  .v-list-group__header.v-list-item--active:before
    opacity: .18

  .v-list-item
    &__icon--text,
    &__icon:first-child
      justify-content: center
      text-align: center
      width: 20px

      +ltr()
        margin-right: 20px
        margin-left: 10px !important

      +rtl()
        margin-left: 20px
        margin-right: 10px !important

  .v-list--dense
    .v-list-item
      &__icon--text,
      &__icon:first-child
        margin-top: 10px

  .v-list-group--sub-group
    .v-list-item
      +ltr()
        padding-left: 8px
      +rtl()
        padding-right: 8px

    .v-list-group__header
      +ltr()
        padding-right: 0
      +rtl()
        padding-right: 0

      .v-list-item__icon--text
        margin-top: 19px
        order: 0

      .v-list-group__header__prepend-icon
        order: 2
        +ltr()
          margin-right: 8px
        +rtl()
          margin-left: 8px

  // Modern list item refinements
  .v-list-item
    border-radius: 10px !important
    margin: 0.1rem 0.75rem

    &__content
      padding: 6px 0

    &__title
      font-weight: 500
      font-size: 0.88rem

    &__icon
      color: inherit !important

  // Group header styling
  .v-list-group
    .v-list-group__header
      border-radius: 10px !important
      padding: 8px 14px
      margin: 0

      &:hover
        background: rgba(49, 130, 206, 0.06)

      &.v-list-item--active
        background: rgba(49, 130, 206, 0.1)
        color: #2b6cb0

        .v-icon
          color: #3182ce

    .v-list-group__header__append-icon
      .v-icon
        transition: transform 0.25s ease

  // Active group arrow rotation
  .v-list-group--active
    > .v-list-group__header
      .v-list-group__header__append-icon .v-icon
        transform: rotate(180deg)
</style>
